import { ModelRow } from "@mcleod/core";
import {
    ChangeEvent,
    CrudDecorator,
    CrudDecoratorCloseEvent,
    DataHeaderProps,
    DataSourceMode,
    Layout,
    TableRow,
    TableRowCreationEvent,
    Textbox,
    DataSource
} from "@mcleod/components";
import { setQuickInfo } from "./DataFusionUtil";
import { isIntercompanyEnabled } from "./EdiUtility";
import { LMECompanySettings } from "@mcleod/general/src/settings/LMECompanySettings";
import { EdiRequiredField } from "./EdiRequiredField";
import { CrudDecoratorProps } from "@mcleod/components/src/page/decorators/CrudDecoratorProps";
import { EDITemplateSlideout, EDITemplateUpdateProps } from "./EDITemplateSlideout";

/**
 * Interface for quick info field mappings used by profile classes
 */
export interface QuickInfoMapping {
    field: string;
    component: Textbox;
    layout: string;
}

/**
 * Interface defining the required context from profile classes that use BaseProfile
 */
export interface ProfileContext {
    mainDataSource: DataSource;
    textboxSendToCompany: Textbox;
    textboxCommId: Textbox;
    tableRequiredField: any; // Table component with required field functionality
    INBOUND: string;
    OUTBOUND: string;
}

/**
 * Base utility class for EDI Profile functionality using composition pattern.
 * This class provides common functionality shared between StatusProfile, TenderProfile, and other EDI profiles.
 *
 * Usage:
 * - Create an instance in the profile class constructor or onLoad
 * - Pass the profile context containing required components and constants
 * - Call methods as needed for common functionality
 *
 * @example
 * ```typescript
 * export class StatusProfile extends AutogenLayoutStatusProfile {
 *     private baseProfile: BaseProfile;
 *
 *     override async onLoad() {
 *         this.baseProfile = new BaseProfile({
 *             mainDataSource: this.mainDataSource,
 *             textboxSendToCompany: this.textboxSendToCompany,
 *             textboxCommId: this.textboxCommId,
 *             tableRequiredField: this.tableRequiredField,
 *             INBOUND: this.INBOUND,
 *             OUTBOUND: this.OUTBOUND
 *         });
 *         await this.baseProfile.initializeIntercompanySupport();
 *     }
 * }
 * ```
 */
export class BaseProfile {
    // Shared EDI constants
    public static readonly EDI_DIRECTIONS = {
        OUTBOUND: "O",
        INBOUND: "I"
    } as const;

    public static readonly TRANSACTION_TYPES = {
        TENDER: "T",
        BILL_OF_LADING: "L",
        FUNCTIONAL_ACK: "F",
        STATUS: "S"
    } as const;

    public static readonly FORMAT_TYPES = {
        XML: "X",
        JSON: "J"
    } as const;

    // Shared properties
    private _isIcEnabled: boolean = false;
    private _isBrokerageLtlCompany: boolean = false;
    private _isAssetLtl: boolean = false;
    private _isBoth: boolean = false;

    // Context from the profile class
    private context: ProfileContext;

    constructor(context: ProfileContext) {
        this.context = context;
    }

    // Property accessors
    public get isIcEnabled(): boolean {
        return this._isIcEnabled;
    }

    public get isBrokerageLtlCompany(): boolean {
        return this._isBrokerageLtlCompany;
    }

    public set isBrokerageLtlCompany(value: boolean) {
        this._isBrokerageLtlCompany = value;
    }

    public get isAssetLtl(): boolean {
        return this._isAssetLtl;
    }

    public set isAssetLtl(value: boolean) {
        this._isAssetLtl = value;
    }

    public get isBoth(): boolean {
        return this._isBoth;
    }

    public set isBoth(value: boolean) {
        this._isBoth = value;
    }

    /**
     * Initializes intercompany support by checking if it's enabled
     * Call this during profile onLoad
     */
    public async initializeIntercompanySupport(): Promise<void> {
        this._isIcEnabled = await isIntercompanyEnabled();
    }

    /**
     * Sets up common table required field handlers for EDI validation rules
     * @param transactionType The transaction type for the profile (e.g., "214", "204", etc.)
     * @param isGroupRule Whether this is for group rules or individual rules
     * @param dialogTitle Optional custom title for the validation rules dialog
     */
    public setupTableRequiredFieldHandlers(transactionType: string, isGroupRule: boolean = false, dialogTitle?: string): void {
        if (!this.context.tableRequiredField) return;

        const defaultTitle = "DataFusion Data Validation Rules";
        const title = dialogTitle || defaultTitle;

        this.context.tableRequiredField.onEditLayoutLoaded = (decorator: any, tableRow: any) => {
            const reqRulesLayout = decorator.layout as EdiRequiredField;
            reqRulesLayout.transactionType = transactionType;
            reqRulesLayout.isGroupRule = isGroupRule;
            reqRulesLayout.version = this.context.mainDataSource.activeRow?.get("version");
            reqRulesLayout.partnerId = this.context.mainDataSource.activeRow?.get("partner_id");
            decorator.labelTitle.text = title;

            const superOnSave = decorator.onSave;
            decorator.onSave = (updatedRow: any) => {
                if (decorator.layout.mainDataSource.validate()) {
                    const rules = reqRulesLayout.tableEdiDataRule?.data;
                    updatedRow.set('data_rules', rules);
                    superOnSave(updatedRow);
                }
            };
        };

        // Set up row creation listener
        this.context.tableRequiredField.addRowCreateListener((event: any) => {
            const tableRow = event.getTableRow();
            if (tableRow.table.editLayout) {
                const originalEditLayoutLoaded = tableRow.table.onEditLayoutLoaded;
                tableRow.table.onEditLayoutLoaded = (decorator: any, row: any) => {
                    const layout = decorator.layout;
                    if (layout instanceof EdiRequiredField) {
                        layout.transactionType = transactionType;
                        layout.version = this.context.mainDataSource.activeRow?.get("version");
                        layout.isGroupRule = isGroupRule;
                        layout.partnerId = this.context.mainDataSource.activeRow?.get("partner_id");
                    }

                    if (originalEditLayoutLoaded) {
                        originalEditLayoutLoaded(decorator, row);
                    }
                };
            }
        });

        // Set up CRUD manager
        this.context.tableRequiredField.crudManager.showCrud = (mode: DataSourceMode, props?: Partial<CrudDecoratorProps>, dataHeaderProps?: Partial<DataHeaderProps>) => {
            const layoutName = "lme/datafusion/EdiRequiredField";
            if (layoutName == null) {
                return;
            }
            const layout = Layout.getLayout(layoutName) as EdiRequiredField;
            layout.version = this.context.mainDataSource.activeRow?.get("version");
            layout.transactionType = transactionType;
            layout.isGroupRule = isGroupRule;
            layout.partnerId = this.context.mainDataSource.activeRow?.get("partner_id");

            const crudDecorator = new CrudDecorator({
                layout: layout,
                mode: mode,
                headerProps: {
                    showClose: true,
                    showSaveAndClose: true,
                    title: title,
                    doAfterClose: (event: CrudDecoratorCloseEvent) => (event) => { },
                    ...dataHeaderProps,
                },
                ...props
            });
            crudDecorator.slideIn({ speed: 200 });
        };

        this.context.tableRequiredField.addButtonCaption = "Add";
    }

    /**
     * Sets quick info for multiple components using provided mappings
     * @param mappings Array of field/component/layout mappings
     * @param customHandler Optional custom handler for specific fields
     */
    public async setQuickInfos(
        mappings: QuickInfoMapping[],
        customHandler?: (field: string, value: any, component: Textbox) => Promise<boolean>
    ): Promise<void> {
        for (const { field, component, layout } of mappings) {
            const value = this.context.mainDataSource?.activeRow?.get(field);
            if (value) {
                if (customHandler && await customHandler(field, value, component)) {
                    // Custom handler processed this field
                    continue;
                }
                setQuickInfo(value, component, layout);
            }
        }
    }

    /**
     * Updates template data from EDI template slideout
     * @param updatedValues The values returned from the template slideout
     */
    public updateFromTemplate(updatedValues: EDITemplateUpdateProps): void {
        const activeRow = this.context.mainDataSource?.activeRow;
        if (!activeRow) return;

        const lookupModelRow = new ModelRow("lme/datafusion/edi-template", false, {
            id: activeRow.get("template_id"),
            name: updatedValues.templateDescription
        });

        activeRow.setLookupModelData("template_id", lookupModelRow);
        activeRow.set("version", updatedValues.version);
        activeRow.set("transaction_set", updatedValues.transactionSet);

        if (activeRow.get("direction") === this.context.INBOUND) {
            activeRow.set("partner_id", updatedValues.appSenderId);
            activeRow.set("alt_partner_id", updatedValues.senderId);
        } else {
            activeRow.set("partner_id", updatedValues.appReceiverId);
            activeRow.set("alt_partner_id", updatedValues.receiverId);
        }
    }

    /**
     * Sets up and shows EDI template slideout
     * @param fieldName The field name containing the template ID
     * @param callback Callback function to handle returned values
     */
    public setupTemplateSlideout(fieldName: string, callback: (returnedValues: EDITemplateUpdateProps) => void): void {
        EDITemplateSlideout.showSlideout({
            templateId: this.context.mainDataSource?.activeRow?.get(fieldName),
            doAfterSlideOut: (returnedValues: EDITemplateUpdateProps) => {
                callback(returnedValues);
            }
        });
    }

    /**
     * Sets company field visibility based on intercompany status
     * @param isEnabled Whether intercompany is enabled
     */
    public setCompanyField(isEnabled: boolean): void {
        this.context.textboxSendToCompany.visible = isEnabled;
        this.context.textboxCommId.visible = !isEnabled;
    }

    /**
     * Sets company settings for LTL operations
     */
    public setCompanySettings(): void {
        const companySettings = LMECompanySettings.getSingleton();
        this.isAssetLtl = companySettings.isAssetLtl();
        this.isBrokerageLtlCompany = companySettings.isBrokerageLtl();
        this.isBoth = companySettings.isBoth();
    }

    /**
     * Common row creation handler for EDI customer tables
     * @param event The table row creation event
     * @param transactionType The transaction type to set
     */
    public handleEdiCustomerRowCreate(event: TableRowCreationEvent, transactionType: string): void {
        const row = event.target as TableRow;
        const activeRow = this.context.mainDataSource?.activeRow;
        if (!activeRow) return;

        row.data.set("alt_partner_id", activeRow.get("alt_partner_id"));
        row.data.set("partner_id", activeRow.get("partner_id"));
        row.data.set("version", activeRow.get("version"));
        row.data.set("direction", activeRow.get("direction"));
        row.data.set("transaction_set", activeRow.get("transaction_set"));
        row.data.set("partner_name", activeRow.get("partner_name"));
        row.data.set("transaction_type", transactionType);
    }

    /**
     * Common row creation handler for required field tables
     * @param event The table row creation event
     * @param transactionSet The transaction set to use
     * @param direction The direction to use (defaults to "O" for outbound)
     */
    public handleRequiredFieldRowCreate(event: TableRowCreationEvent, transactionSet: string, direction: string = "O"): void {
        const row = event.target as TableRow;
        const activeRow = this.context.mainDataSource?.activeRow;
        if (!activeRow) return;

        // Use reply partner information if available, otherwise use main partner info
        const partnerId = activeRow.get("reply_partner_id") || activeRow.get("partner_id");
        const altPartnerId = activeRow.get("reply_altpartnerid") || activeRow.get("alt_partner_id");
        const version = activeRow.get("reply_version") || activeRow.get("version");

        row.data.set("partner_id", partnerId);
        row.data.set("alt_partner_id", altPartnerId);
        row.data.set("version", version);
        row.data.set("transaction_set", transactionSet);
        row.data.set("direction", direction);
    }

    /**
     * Handles intercompany change events
     * @param event The change event
     * @param setCompanyFieldCallback Callback to update company field visibility
     */
    public handleIntercompanyChange(event: ChangeEvent, setCompanyFieldCallback: (enabled: boolean) => void): void {
        if (event.userInitiatedChange) {
            setCompanyFieldCallback(event.newValue);
        }
    }

    /**
     * Checks if the current direction is inbound
     * @returns true if direction is inbound
     */
    public isInboundDirection(): boolean {
        return this.context.mainDataSource?.activeRow?.get("direction") === this.context.INBOUND;
    }

    /**
     * Checks if the current direction is outbound
     * @returns true if direction is outbound
     */
    public isOutboundDirection(): boolean {
        return this.context.mainDataSource?.activeRow?.get("direction") === this.context.OUTBOUND;
    }
}
